
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ProfilePrice {
  id: string;
  profile_report_id: string;
  commodity_name: string;
  variety: string | null;
  group_name: string | null;
  price: number;
  unit: string;
  market_type: string;
  report_date: string;
  market_city: string;
  grade: string | null;
  package_type: string | null;
  origin: string | null;
  created_at: string;
  updated_at: string;
}

export const useProfilePrices = () => {
  const [profilePrices, setProfilePrices] = useState<ProfilePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfilePrices = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfilePrices([]);
        return;
      }

      // Get user profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) {
        console.log('No profile found for user');
        setProfilePrices([]);
        return;
      }

      // Fetch profile prices through profile reports
      const { data: prices, error } = await supabase
        .from("profile_prices")
        .select(`
          *,
          profile_reports!inner(
            profile_id,
            market_city,
            report_name
          )
        `)
        .eq("profile_reports.profile_id", profile.id)
        .order("report_date", { ascending: false });

      if (error) throw error;

      console.log('Profile prices fetched:', prices?.length || 0);
      setProfilePrices(prices || []);
    } catch (err) {
      console.error("Error fetching profile prices:", err);
      setError('Failed to load profile prices');
    } finally {
      setLoading(false);
    }
  };

  const storeProfilePrices = async (profileReportId: string, prices: any[]) => {
    try {
      console.log(`Storing ${prices.length} prices for profile report ${profileReportId}`);
      
      // Transform the prices to match our schema
      const priceRecords = prices.map(price => ({
        profile_report_id: profileReportId,
        commodity_name: price.commodity_name || 'Unknown',
        variety: price.variety || null,
        group_name: price.group_name || null,
        price: parseFloat(price.current_price || price.price || 0),
        unit: price.unit || 'per lb',
        market_type: price.market_type || 'terminal',
        report_date: price.report_date || new Date().toISOString().split('T')[0],
        market_city: price.market_city || 'Unknown',
        grade: price.grade || null,
        package_type: price.package_type || null,
        origin: price.origin || null
      }));

      // Insert the price records
      const { error } = await supabase
        .from("profile_prices")
        .insert(priceRecords);

      if (error) throw error;

      console.log(`Successfully stored ${priceRecords.length} profile prices`);
      
      // Refresh the profile prices
      await fetchProfilePrices();
    } catch (err) {
      console.error("Error storing profile prices:", err);
      throw err;
    }
  };

  const clearProfilePrices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) return;

      // Get all profile report IDs for this profile
      const { data: profileReports } = await supabase
        .from("profile_reports")
        .select("id")
        .eq("profile_id", profile.id);

      if (!profileReports || profileReports.length === 0) return;

      const profileReportIds = profileReports.map(report => report.id);

      // Delete prices for this profile's reports
      const { error } = await supabase
        .from("profile_prices")
        .delete()
        .in("profile_report_id", profileReportIds);

      if (error) throw error;

      console.log('Profile prices cleared');
      await fetchProfilePrices();
    } catch (err) {
      console.error("Error clearing profile prices:", err);
      throw err;
    }
  };

  useEffect(() => {
    fetchProfilePrices();
  }, []);

  return {
    profilePrices,
    loading,
    error,
    fetchProfilePrices,
    storeProfilePrices,
    clearProfilePrices
  };
};
